import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleNumber } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Card, Divider, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useDownloadBusinessCoefficientsExport,
  useFilterBusinessCoefficientsReport,
} from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { CommissionKindTag } from "../../../commissions/batch/components/CommissionKindTag";
import {
  BusinessCoefficientReport,
  BusinessCoefficientsReportFilterPageRequest,
} from "../../../commissions/productcommissionssettings/types";

const formatValueRange = (t: TFunction, valueFrom?: number, valueTo?: number) => {
  if (isDefined(valueFrom) && isDefined(valueTo)) {
    return `${valueFrom} - ${valueTo}`;
  }

  if (isDefined(valueFrom)) {
    return `${t("common.from")} ${valueFrom}`;
  }

  if (isDefined(valueTo)) {
    return `${t("common.to")} ${valueTo}`;
  }

  return undefined;
};

type Props = {
  searchParams: BusinessCoefficientsReportFilterPageRequest;
};

export const BusinessCoefficientsTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { data, isFetching } = useFilterBusinessCoefficientsReport(searchParams);
  const downloadBusinessCoefficientsExport = useDownloadBusinessCoefficientsExport();

  const columns = useMemo(() => {
    const items: ColumnsType<BusinessCoefficientReport> = [
      {
        key: "sector",
        title: t("overviews.businessCoefficients.attrs.sector"),
        width: 150,
        render: (_, record) => (
          <Ellipsis>{t("product.enums.financialSector." + record.settings.product.sector)}</Ellipsis>
        ),
      },
      {
        key: "institutions",
        title: t("overviews.businessCoefficients.attrs.institutions"),
        width: 150,
        render: (_, record) => (
          <Ellipsis>{record.settings.product.institutions.map((institution) => institution.name).join(", ")}</Ellipsis>
        ),
      },
      {
        key: "productGroup",
        title: t("overviews.businessCoefficients.attrs.productGroup"),
        width: 150,
        render: (_, record) => <Ellipsis>{record.settings.product.productGroup.name}</Ellipsis>,
      },
      {
        key: "product",
        title: t("overviews.businessCoefficients.attrs.product"),
        width: 240,
        render: (_, record) => <Ellipsis>{record.settings.product.name}</Ellipsis>,
      },
      {
        key: "commissionKind",
        title: t("overviews.businessCoefficients.attrs.commissionKind"),
        width: 210,
        align: "center",
        render: (_, record) => <CommissionKindTag kind={record.settings.commissionKind} />,
      },
      {
        key: "valueRange",
        title: t("overviews.businessCoefficients.attrs.valueRange"),
        width: 130,
        align: "center",
        render: (_, record) => formatValueRange(t, record.valueFrom, record.valueTo),
      },
      {
        key: "coefficient",
        title: t("overviews.businessCoefficients.attrs.coefficient"),
        width: 120,
        align: "right",
        fixed: "right",
        render: (_, record) => formatLocaleNumber(record.coefficient, 6),
      },
    ];

    return items;
  }, [t]);

  return (
    <Card className="card-box">
      <div className={data?.totalElementsCount ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() =>
            downloadBusinessCoefficientsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
          }
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() =>
            downloadBusinessCoefficientsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
          }
        />
      </div>

      <Table<BusinessCoefficientReport>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
